// La crew in testa alla chat della sessione (tela, «Sessione — la crew al lavoro»): chi c'è, chi
// sta lavorando (il pop pulsa), chi ha finito e chi aspetta; un clic dice cosa sta facendo. In
// fondo ai messaggi `Working` mostra chi sta lavorando adesso.
import { useEffect, useState } from 'react';
import { Pop } from '../../components/Pop';
import type { AgentProfile } from '../../lib/api';
import { popName, popTitle } from '../../lib/usePopAvatars';
import { crewOf, type CrewMember } from '../../lib/crew';
import type { MissionTask, Session, TraceEntry } from '../../types';
import { faceOf } from '../control/faces';

const LABEL: Record<CrewMember['state'], string> = {
  working: 'al lavoro',
  done: 'ha finito',
  idle: 'in attesa',
};

/** I membri, ricalcolati ogni 15 secondi: «al lavoro» dipende anche da quanto è passato. */
export function useCrew(s: Session, tasks: MissionTask[] | undefined, trace: TraceEntry[]) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);
  return crewOf(s, tasks, trace, now);
}

const taskRole = (name: string, tasks: MissionTask[] | undefined) =>
  tasks?.find((t) => t.agent === name)?.role ?? null;

export function Crew({
  crew,
  tasks,
  profiles,
}: {
  crew: CrewMember[];
  tasks: MissionTask[] | undefined;
  profiles: AgentProfile[];
}) {
  const [picked, setPicked] = useState<string | null>(null);
  if (crew.length === 0) return null;
  const sel =
    crew.find((m) => m.name === picked) ?? crew.find((m) => m.state === 'working') ?? crew[0];
  const busy = crew.filter((m) => m.state === 'working').length;
  const selFace = faceOf(sel.name, tasks, profiles);
  return (
    <section className="crew" aria-label="La crew">
      <div className="crew-head">
        <h2 className="crew-title">La crew</h2>
        <span className="t-caption dim">
          {crew.length} pop · {busy} al lavoro
        </span>
      </div>
      <div className="crew-row">
        {crew.map((m) => {
          const face = faceOf(m.name, tasks, profiles);
          return (
            <button
              key={m.name}
              type="button"
              className="crew-chip"
              data-on={m.name === sel.name ? 'true' : undefined}
              aria-pressed={m.name === sel.name}
              onClick={() => setPicked(m.name)}
            >
              <span className="crew-face" data-state={m.state}>
                {face && <Pop avatar={face} size={40} />}
              </span>
              <span className="crew-who">
                <span className="crew-name">
                  {popName(m.name, profiles, taskRole(m.name, tasks)).name}
                </span>
                <span className="crew-state" data-state={m.state}>
                  {LABEL[m.state]}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="crew-detail">
        {selFace && <Pop avatar={selFace} size={32} />}
        <span style={{ minWidth: 0 }}>
          <span className="t-strong" style={{ display: 'block' }}>
            {popTitle(sel.name, profiles, taskRole(sel.name, tasks))}
          </span>
          <span className="t-small dim trunc" style={{ display: 'block' }}>
            {sel.what}
          </span>
        </span>
      </div>
    </section>
  );
}

/** Chi sta lavorando adesso, in fondo alla chat: il pop pulsa e dice cosa fa. */
export function Working({
  crew,
  tasks,
  profiles,
}: {
  crew: CrewMember[];
  tasks: MissionTask[] | undefined;
  profiles: AgentProfile[];
}) {
  const busy = crew.filter((m) => m.state === 'working');
  if (busy.length === 0) return null;
  return (
    <div aria-live="polite" style={{ display: 'grid', gap: 10 }}>
      {busy.map((m) => {
        const face = faceOf(m.name, tasks, profiles);
        return (
          <div key={m.name} className="crew-working">
            <span className="crew-face" data-state="working">
              {face && <Pop avatar={face} size={44} />}
            </span>
            <span className="crew-working-bubble">
              <span className="crew-working-who">
                {popTitle(m.name, profiles, taskRole(m.name, tasks))} sta lavorando
                <span className="crew-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </span>
              <span className="t-small dim">{m.what}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
